/**
 * Team page calls (XC-TEAM-INVITES Phase 1): the team, its Activity, invites, Member requests, role
 * changes and deactivation. Every call is signed-in; the server decides what each role may do, so a
 * refusal is shown, never guessed in advance. Replies are checked against `types/team.ts`.
 */

import axios from 'axios';
import { z } from 'zod';

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import {
  ActivitySchema,
  InviteResultSchema,
  TeamSchema,
  TeamUsageSchema,
  type ActivityItem,
  type InviteResult,
  type Team,
  type TeamRole,
  type TeamUsage,
  type UsageRange,
} from '@/types/team';

export const TEAM_PATH = '/api/v1/team';

const teamApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

teamApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** A reply off the contract: nothing of it is shown. */
export class TeamContractError extends Error {
  constructor(public readonly issues: string[]) {
    super(`team reply failed its contract: ${issues.join('; ')}`);
    this.name = 'TeamContractError';
  }
}

function parsed<T>(schema: z.ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new TeamContractError(result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`));
  }
  return result.data;
}

export async function fetchTeam(): Promise<Team> {
  const { data } = await teamApi.get<unknown>(TEAM_PATH);
  return parsed(TeamSchema, data);
}

export async function fetchActivity(limit = 50): Promise<ActivityItem[]> {
  const { data } = await teamApi.get<unknown>(`${TEAM_PATH}/activity`, { params: { limit } });
  return parsed(ActivitySchema, data).items;
}

/** The team's use over time, in the viewer's own time zone (every role: the pre-launch rule). */
export async function fetchUsage(days: UsageRange, zone: string): Promise<TeamUsage> {
  const { data } = await teamApi.get<unknown>(`${TEAM_PATH}/usage`, { params: { days, tz: zone } });
  return parsed(TeamUsageSchema, data);
}

export async function sendInvites(
  emails: string[],
  role: TeamRole,
  note: string | null,
): Promise<InviteResult> {
  const { data } = await teamApi.post<unknown>(`${TEAM_PATH}/invites`, {
    emails,
    role,
    note: note && note.trim() ? note.trim() : null,
  });
  return parsed(InviteResultSchema, data);
}

export type InviteAct = 'resend' | 'cancel';

export async function actOnInvite(inviteId: string, act: InviteAct): Promise<void> {
  await teamApi.post(`${TEAM_PATH}/invites/${encodeURIComponent(inviteId)}/${act}`);
}

export async function approveRequest(inviteId: string, role?: TeamRole): Promise<void> {
  await teamApi.post(`${TEAM_PATH}/invite-requests/${encodeURIComponent(inviteId)}/approve`, {
    role: role ?? null,
  });
}

export async function declineRequest(inviteId: string): Promise<void> {
  await teamApi.post(`${TEAM_PATH}/invite-requests/${encodeURIComponent(inviteId)}/decline`, {});
}

export async function changeRole(userId: string, role: TeamRole): Promise<void> {
  await teamApi.patch(`/api/v1/users/${encodeURIComponent(userId)}`, { role });
}

export async function setActive(userId: string, active: boolean): Promise<void> {
  const act = active ? 'reactivate' : 'deactivate';
  await teamApi.post(`/api/v1/users/${encodeURIComponent(userId)}/${act}`);
}

/** Plain words for a refused team call: the server's own reason when it gives one. */
export function teamErrorMessage(err: unknown): string {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (!response) return "Can't reach AskWingo right now. Check your connection and try again.";
  const detail = response.data?.detail;
  if (response.status === 403) {
    return typeof detail === 'string' ? detail : 'Only Owners and Admins can do this.';
  }
  if (response.status === 409 && typeof detail === 'string') return detail;
  if (response.status === 404) return 'That person or invite is no longer here. Refresh the page.';
  return 'Something went wrong. Try again.';
}
